import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, Check, Lock, Share2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { track } from "../lib/analytics";
import { celebrate } from "../lib/celebrate";

const DAYS = [
  {
    n: 1,
    title: "Define your top 5 life goals",
    prompt: "Write down one goal for each pillar — financial, health, spirituality, relationships, endgame. Don't optimize the wording. Just get them out of your head.",
    action: "List 5 goals.",
  },
  {
    n: 2,
    title: "Build your health routine",
    prompt: "Pick ONE health habit you can do every day for the rest of the week. Sleep, water, walk, lift, stretch, sober — your call. One.",
    action: "Commit to one health habit.",
  },
  {
    n: 3,
    title: "Organize your money plan",
    prompt: "Write down: what's coming in, what's going out, and what's the next $100 you can save or stop bleeding. No spreadsheets needed today — just clarity.",
    action: "Name your money next-step.",
  },
  {
    n: 4,
    title: "Fix your daily schedule",
    prompt: "Block 3 things in tomorrow's calendar: one for your goal, one for your health, one for someone you love. Nothing else needs to move yet.",
    action: "Block 3 things.",
  },
  {
    n: 5,
    title: "Improve one relationship",
    prompt: "Send one message to one person who matters and isn't getting enough of you. No agenda. Just contact.",
    action: "Send one message.",
  },
  {
    n: 6,
    title: "Create your spirituality / reflection habit",
    prompt: "Pick a 5-minute practice — silence, prayer, journaling, breath, walk without phone — and do it today. Same time tomorrow if it lands.",
    action: "Do 5 minutes today.",
  },
  {
    n: 7,
    title: "Build your next 30-day roadmap",
    prompt: "Look at your 5 goals from Day 1. Pick the one that scares you most, and write the FIRST step you'll take in the next 30 days. That's your roadmap. The AI coach can help you flesh out the rest.",
    action: "Write your 30-day first step.",
  },
];

export default function ChallengePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [enrolled, setEnrolled] = useState(false);
  const [progress, setProgress] = useState({ current_day: 1, completed_days: [] });
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [openDay, setOpenDay] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "7-Day Ascend Challenge — Get Your Life Organized";
    track("challenge_page_view");
    if (user) {
      (async () => {
        try {
          const r = await axios.get(`${API}/challenge/me`);
          if (r.data.enrolled) {
            setEnrolled(true);
            setProgress({
              current_day: r.data.current_day,
              completed_days: r.data.completed_days,
            });
            setOpenDay(r.data.current_day);
          }
        } catch (e) {}
      })();
    }
  }, [user]);

  const enroll = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      const payload = user ? {} : { email, name };
      if (!user && (!email || !email.includes("@"))) {
        toast.error("Valid email please.");
        setLoading(false);
        return;
      }
      const r = await axios.post(`${API}/challenge/signup`, payload);
      setEnrolled(true);
      setProgress({
        current_day: r.data.current_day || 1,
        completed_days: r.data.completed_days || [],
      });
      track("challenge_signup_completed", { authed: !!user });
      celebrate("normal");
      toast.success("You're in. Day 1 is unlocked.");
    } catch (err) {
      toast.error("Couldn't start the challenge. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const completeDay = async (day) => {
    if (!user) {
      toast.message("Sign in to track your progress.", {
        action: { label: "Sign in", onClick: () => navigate("/start") },
      });
      return;
    }
    try {
      await axios.post(`${API}/challenge/complete-day`, { day });
      setProgress((p) => ({
        ...p,
        completed_days: [...new Set([...(p.completed_days || []), day])],
      }));
      track("challenge_day_completed", { day });
      if (day === 7) {
        celebrate("big");
        toast.success("Challenge complete. Build your real roadmap →");
      } else {
        celebrate("normal");
      }
    } catch (e) {
      toast.error("Couldn't save.");
    }
  };

  const completedCount = (progress.completed_days || []).length;

  return (
    <div className="min-h-screen bg-[#f7f3ea]" data-testid="challenge-page">
      <header className="px-6 py-6 max-w-5xl mx-auto flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} className="inline-flex items-center gap-2 font-body text-[14px] text-[#2b3442] hover:text-[#486a8f]">
          <ArrowLeft size={16} /> {user ? "Dashboard" : "Back"}
        </Link>
        <div className="flex items-center gap-1">
          <span className="font-display text-[20px] font-semibold text-[#0f1724]">ascend</span>
          <span className="font-display text-[20px] font-semibold text-[#486a8f]">higher</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 pb-24">
        <div className="text-center mb-10">
          <p className="font-mono-eyebrow text-[#486a8f] mb-3">FREE · 7-DAY CHALLENGE</p>
          <h1 className="font-display text-[44px] md:text-[60px] leading-[1.05] text-[#0f1724] tracking-tight">
            7 days to get your{" "}
            <span className="italic font-light text-[#486a8f]">life organized.</span>
          </h1>
          <p className="font-body text-[15px] md:text-[16px] text-[#4b5563] mt-5 max-w-xl mx-auto leading-relaxed">
            One small action per day. No app required to start. Each day takes under 10 minutes
            and clears one piece of the chaos.
          </p>
        </div>

        {!enrolled ? (
          <form
            onSubmit={enroll}
            className="rounded-2xl bg-white border border-[#eadfc9] p-7 md:p-9 mb-10"
          >
            <p className="font-display text-[20px] text-[#0f1724] mb-4">
              Start your 7-day challenge
            </p>
            {!user && (
              <>
                <Input
                  data-testid="challenge-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="h-11 mb-3"
                />
                <Input
                  data-testid="challenge-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="First name (optional)"
                  className="h-11 mb-4"
                />
              </>
            )}
            <Button
              data-testid="challenge-enroll-btn"
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-[#3e6b52] hover:bg-[#345a45] text-white font-medium"
            >
              {loading ? "Starting…" : "Start the challenge"}
              <ArrowRight size={15} className="ml-2" />
            </Button>
            <p className="font-body text-[11px] text-[#9aa3ae] text-center mt-4">
              Free challenge. Personal growth tool — not therapy or medical care.
            </p>
          </form>
        ) : (
          <div className="rounded-2xl bg-[#e9f0ec] border border-[#c8d6cc] p-5 mb-8 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shrink-0">
              <span className="font-display text-[18px] text-[#3e6b52]">{completedCount}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-body text-[13px] font-semibold text-[#0f1724]">
                You're on day {progress.current_day} of 7
              </p>
              <p className="font-body text-[12px] text-[#4b5563]">
                {completedCount === 7 ? "Challenge complete. Build your real roadmap." : `${completedCount} of 7 days complete.`}
              </p>
            </div>
            {completedCount === 7 && (
              <Link to="/start">
                <Button className="h-9 px-4 rounded-full bg-[#3e6b52] hover:bg-[#345a45] text-white text-[12px]">
                  Build my roadmap
                </Button>
              </Link>
            )}
          </div>
        )}

        {/* Days */}
        <div className="space-y-3">
          {DAYS.map((d) => {
            const done = (progress.completed_days || []).includes(d.n);
            const locked = enrolled && d.n > progress.current_day;
            const isOpen = openDay === d.n;
            return (
              <div
                key={d.n}
                className={`rounded-2xl border overflow-hidden ${
                  done ? "bg-[#f0e9d6] border-[#d9cfb8]" : "bg-white border-[#eadfc9]"
                }`}
              >
                <button
                  data-testid={`challenge-day-toggle-${d.n}`}
                  onClick={() => setOpenDay(isOpen ? -1 : d.n)}
                  className="w-full text-left p-5 md:p-6 flex items-center gap-4"
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                      done ? "bg-[#3e6b52] text-white" : locked ? "bg-[#faf6ec] text-[#9aa3ae]" : "bg-[#e7edf5] text-[#486a8f]"
                    }`}
                  >
                    {done ? <Check size={16} /> : locked ? <Lock size={14} /> : <span className="font-display text-[15px]">{d.n}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-mono-eyebrow text-[#486a8f]">DAY {d.n}</p>
                    <p className="font-display text-[18px] md:text-[20px] text-[#0f1724] tracking-tight leading-tight">
                      {d.title}
                    </p>
                  </div>
                </button>
                {isOpen && !locked && (
                  <div className="px-6 md:px-7 pb-6 -mt-1">
                    <p className="font-body text-[14px] text-[#4b5563] leading-relaxed mb-4">
                      {d.prompt}
                    </p>
                    <p className="font-body text-[13px] text-[#0f1724] font-medium mb-5">
                      → {d.action}
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      {!done ? (
                        <Button
                          data-testid={`challenge-day-complete-${d.n}`}
                          onClick={() => completeDay(d.n)}
                          className="h-10 px-5 rounded-full bg-[#3e6b52] hover:bg-[#345a45] text-white text-[13px] font-medium"
                        >
                          <Check size={14} className="mr-2" /> Mark Day {d.n} complete
                        </Button>
                      ) : (
                        <span className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-[#e9f0ec] text-[#3e6b52] text-[12px] font-medium">
                          <Check size={13} /> Day {d.n} done
                        </span>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => {
                          const text = `Day ${d.n} of 7 of the Ascend Higher Challenge: ${d.title}. Doing this with me?`;
                          const url = window.location.href;
                          if (navigator.share) {
                            navigator.share({ text, url }).catch(() => {});
                          } else {
                            navigator.clipboard.writeText(`${text} ${url}`);
                            toast.success("Day post copied.");
                          }
                          track("challenge_day_shared", { day: d.n });
                        }}
                        className="h-10 px-4 rounded-full border-[#d9cfb8] text-[12px]"
                      >
                        <Share2 size={13} className="mr-2" /> Share
                      </Button>
                    </div>
                  </div>
                )}
                {isOpen && locked && (
                  <div className="px-6 pb-6 -mt-1">
                    <p className="font-body text-[13px] text-[#6b7280]">
                      Unlocks on day {d.n} of your challenge.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Final CTA */}
        <div className="mt-12 rounded-2xl bg-gradient-to-br from-[#0f1724] to-[#1c2944] text-white p-8 text-center">
          <p className="font-mono-eyebrow text-[#8aa7c4] mb-3">DAY 8 AND BEYOND</p>
          <h3 className="font-display text-[28px] tracking-tight leading-tight mb-3">
            Don't lose the momentum.
          </h3>
          <p className="font-body text-[14px] text-white/80 mb-6 max-w-md mx-auto">
            Drop your 7 days into Ascend and your AI coach will turn it into a daily roadmap
            that adjusts as life changes.
          </p>
          <Link to="/start">
            <Button
              data-testid="challenge-upgrade-cta"
              onClick={() => track("challenge_upgrade_cta_click")}
              className="h-12 px-7 rounded-full bg-white hover:bg-[#faf6ec] text-[#0f1724] text-[14px] font-medium"
            >
              Build my real roadmap <ArrowRight size={14} className="ml-2" />
            </Button>
          </Link>
          <p className="font-body text-[11px] text-white/60 mt-4">3 days free · Card required at checkout.</p>
        </div>
      </main>
    </div>
  );
}
